import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useNavigate, Link } from 'react-router-dom'
import {
  isValidEmail,
  isValidPhoneMx,
  isValidUUID,
  passwordStrength,
  checkClientRateLimit,
} from '../lib/validation'
import PasswordInput from '../components/PasswordInput'

const FP = "'Playfair Display', Georgia, serif"
const FB = "'DM Sans', system-ui, sans-serif"

async function getClientIP() {
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 3000)
    const res = await fetch('https://api.ipify.org?format=json', { signal: controller.signal })
    clearTimeout(timer)
    const { ip } = await res.json()
    return ip
  } catch {
    return null
  }
}

export default function Register() {
  const navigate = useNavigate()

  const searchParams = new URLSearchParams(window.location.search)
  const rolParam = searchParams.get('rol')
  const [role, setRole] = useState(rolParam === 'provider' ? 'provider' : 'client')

  const [fullName, setFullName] = useState('')
  const [phone, setPhone]       = useState('')
  const [email, setEmail]       = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading]   = useState(false)
  const [error, setError]       = useState(null)

  const [step, setStep]             = useState('form')
  const [otpCode, setOtpCode]       = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const [normalizedPhone, setNormalizedPhone] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)

    if (!isValidEmail(email)) {
      setError('Correo inválido')
      setLoading(false)
      return
    }
    const pwd = passwordStrength(password)
    if (!pwd.ok) {
      setError(pwd.reason)
      setLoading(false)
      return
    }
    if (!checkClientRateLimit('bellason_register_attempts', 3, 10 * 60 * 1000)) {
      setError('Demasiados intentos. Espera unos minutos.')
      setLoading(false)
      return
    }

    // Clients register directly — no OTP, but phone is required for contact unlock
    if (role === 'client') {
      if (!isValidPhoneMx(phone)) {
        setError('El teléfono debe tener 10 dígitos')
        setLoading(false)
        return
      }
      const { data: existing } = await supabase
        .from('users')
        .select('id')
        .eq('phone', phone)
        .maybeSingle()
      if (existing) {
        setError('Este número ya está registrado. Si es tuyo, inicia sesión.')
        setLoading(false)
        return
      }
      await registerUser(phone)
      setLoading(false)
      return
    }

    // Providers must verify phone via OTP
    if (!isValidPhoneMx(phone)) {
      setError('El teléfono debe tener 10 dígitos')
      setLoading(false)
      return
    }

    const { data: existing } = await supabase
      .from('users')
      .select('id')
      .eq('phone', phone)
      .maybeSingle()
    if (existing) {
      setError('Este número ya está registrado. Si es tuyo, inicia sesión.')
      setLoading(false)
      return
    }

    try {
      const { data, error: fnErr } = await supabase.functions.invoke('send-twilio-otp', {
        body: { phone },
      })
      if (fnErr || data?.error) {
        setError(data?.error || 'No se pudo enviar el código. Intenta de nuevo.')
        setLoading(false)
        return
      }
      setNormalizedPhone(data.phone || phone)
      setStep('otp')
    } catch {
      setError('Error de conexión. Intenta de nuevo.')
    }
    setLoading(false)
  }

  async function registerUser(verifiedPhone) {
    let referredBy = null
    try {
      const ref = localStorage.getItem('bellason_ref')
      if (ref && isValidUUID(ref)) referredBy = ref
    } catch { /* localStorage blocked */ }

    const ip = await getClientIP()

    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/login`,
        data: { role, full_name: fullName, phone: verifiedPhone || '' },
      },
    })

    if (authError) {
      const msg = authError.message || ''
      if (msg.includes('23505') || msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('already')) {
        setError('Este correo ya está registrado')
      } else {
        setError(msg)
      }
      return
    }

    if (referredBy && referredBy !== authData.user.id) {
      await supabase.from('users')
        .update({ registration_ip: ip, referred_by: referredBy })
        .eq('id', authData.user.id)
    } else if (ip) {
      await supabase.from('users')
        .update({ registration_ip: ip })
        .eq('id', authData.user.id)
    }

    if (!authData.session) {
      navigate('/login?verify=1')
      return
    }

    if (role === 'client') {
      navigate('/nueva-solicitud')
    } else {
      navigate('/mi-perfil')
    }
  }

  async function handleVerifyAndRegister(e) {
    e.preventDefault()
    setOtpLoading(true)
    setError(null)

    if (!otpCode || otpCode.length < 4) {
      setError('Ingresa el código de verificación')
      setOtpLoading(false)
      return
    }

    const { data: verifyData, error: verifyErr } = await supabase.functions.invoke('verify-twilio-otp', {
      body: { phone: normalizedPhone || phone, code: otpCode },
    })
    if (verifyErr || verifyData?.error) {
      setError(verifyData?.error || 'Código incorrecto.')
      setOtpLoading(false)
      return
    }

    await registerUser(verifyData.phone || normalizedPhone || phone)
    setOtpLoading(false)
  }

  async function handleResendOtp() {
    setError(null)
    setOtpCode('')
    const { data, error: fnErr } = await supabase.functions.invoke('send-twilio-otp', {
      body: { phone },
    })
    if (fnErr || data?.error) {
      setError(data?.error || 'No se pudo reenviar el código.')
    } else {
      setNormalizedPhone(data.phone || phone)
    }
  }

  // ─── OTP step (providers only) ──────────────────────────────────────
  if (step === 'otp') {
    return (
      <div style={S.container}>
        <div style={S.card}>
          <button type="button" onClick={() => { setStep('form'); setError(null); setOtpCode('') }} style={S.backBtn}>
            ← Volver
          </button>

          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ fontSize: '2rem', marginBottom: '12px' }}>📱</div>
            <h1 style={S.title}>Verificar número</h1>
            <p style={S.subtitle}>
              Enviamos un código SMS al<br />
              <strong style={{ color: 'var(--bln-accent)', fontFamily: FP, fontStyle: 'italic' }}>+52 {phone}</strong>
            </p>
          </div>

          <form onSubmit={handleVerifyAndRegister} style={S.form}>
            <label htmlFor="otp-code" style={S.srOnly}>Código de verificación</label>
            <input
              id="otp-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              style={{ ...S.input, textAlign: 'center', fontSize: '2rem', letterSpacing: '0.4em', fontWeight: '300', fontFamily: FP }}
              placeholder="000000"
              value={otpCode}
              onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
            />

            {error && <p style={S.error}>⚠ {error}</p>}

            <button type="submit" disabled={otpLoading} style={S.btnPrimary}>
              {otpLoading ? 'Verificando…' : 'Verificar y crear cuenta'}
            </button>
          </form>

          <p style={{ ...S.link, marginTop: '1.25rem', textAlign: 'center' }}>
            ¿No recibiste el código?{' '}
            <button
              type="button"
              style={{ background: 'none', border: 'none', color: 'var(--bln-accent)', cursor: 'pointer', fontWeight: '500', fontSize: 'inherit', fontFamily: FB }}
              onClick={handleResendOtp}
            >
              Reenviar
            </button>
          </p>
        </div>
      </div>
    )
  }

  // ─── Form step ──────────────────────────────────────────────────────
  return (
    <div style={S.container}>
      <div style={S.card}>

        <button type="button" onClick={() => navigate(-1)} style={S.backBtn}>← Volver</button>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src="/logo-v1.png" alt="Bellason" width={36} height={36} style={{ display: 'inline-block', marginBottom: '12px', objectFit: 'contain' }} />
          <h1 style={S.title}>Bellason</h1>
          <p style={S.subtitle}>Crea tu cuenta</p>
        </div>

        <div style={S.roleContainer}>
          <button type="button" style={role === 'client' ? S.roleActive : S.role} onClick={() => { setRole('client'); setError(null) }}>
            Soy cliente
          </button>
          <button type="button" style={role === 'provider' ? S.roleActive : S.role} onClick={() => { setRole('provider'); setError(null) }}>
            Soy profesional
          </button>
        </div>

        <div style={S.roleInfo}>
          {role === 'client' ? (
            <span>Publicar solicitudes es <em style={{ color: 'var(--bln-accent)', fontFamily: FP }}>gratis</em></span>
          ) : (
            <span>Tienes <em style={{ color: 'var(--bln-accent)', fontFamily: FP }}>5 leads gratis</em> al registrarte</span>
          )}
        </div>

        <form onSubmit={handleSubmit} style={S.form}>
          <label htmlFor="reg-fullName" style={S.srOnly}>Nombre completo</label>
          <input id="reg-fullName" name="fullName" autoComplete="name"
            style={S.input} placeholder="Nombre completo"
            value={fullName} onChange={e => setFullName(e.target.value)} required />

          <label htmlFor="reg-phone" style={S.srOnly}>Teléfono (10 dígitos)</label>
          <input id="reg-phone" name="phone" autoComplete="tel-national"
            style={S.input} placeholder="Teléfono (10 dígitos)" type="tel"
            value={phone} onChange={e => setPhone(e.target.value)}
            maxLength={10} inputMode="numeric" required />

          <label htmlFor="reg-email" style={S.srOnly}>Correo electrónico</label>
          <input id="reg-email" name="email" autoComplete="email"
            style={S.input} placeholder="Correo electrónico" type="email"
            value={email} onChange={e => setEmail(e.target.value)} required />

          <label htmlFor="reg-password" style={S.srOnly}>Contraseña</label>
          <PasswordInput id="reg-password" name="password"
            style={S.input} placeholder="Contraseña (mín. 8, letras y números)"
            value={password} onChange={e => setPassword(e.target.value)}
            minLength={8} required autoComplete="new-password" />

          {error && <p style={S.error}>⚠ {error}</p>}

          <button type="submit" disabled={loading} style={S.btnPrimary}>
            {loading
              ? (role === 'client' ? 'Creando cuenta…' : 'Enviando código…')
              : (role === 'client' ? 'Crear cuenta' : 'Continuar')}
          </button>

          <p style={{ ...S.link, fontSize: '11px', marginTop: '6px' }}>
            Al crear tu cuenta aceptas los{' '}
            <Link to="/terminos" style={{ color: 'var(--bln-accent)' }}>
              Términos y Condiciones
            </Link>.
          </p>
        </form>

        <p style={S.link}>
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" style={{ color: 'var(--bln-accent)' }}>Inicia sesión</Link>
        </p>
      </div>
    </div>
  )
}

const S = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--bln-bg)',
    padding: '1.5rem 1rem',
    fontFamily: FB,
  },
  card: {
    backgroundColor: 'var(--bln-bg)',
    padding: '2.5rem 2rem',
    borderRadius: '14px',
    width: '100%',
    maxWidth: '400px',
    border: '1px solid var(--bln-accent-tint)',
  },
  title: {
    fontFamily: FP,
    color: 'var(--bln-text)',
    fontSize: '1.8rem',
    fontWeight: 400,
    textAlign: 'center',
    margin: '0 0 4px',
    letterSpacing: '-0.3px',
  },
  subtitle: {
    color: 'var(--bln-text-muted)',
    textAlign: 'center',
    margin: 0,
    fontSize: '13px',
    fontWeight: 300,
    lineHeight: 1.6,
  },
  roleContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '10px',
  },
  role: {
    flex: 1,
    padding: '10px',
    border: '1px solid var(--bln-accent-tint)',
    borderRadius: '8px',
    backgroundColor: 'transparent',
    color: 'var(--bln-text-muted)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 300,
    fontFamily: FB,
    letterSpacing: '0.2px',
  },
  roleActive: {
    flex: 1,
    padding: '10px',
    border: '1px solid var(--bln-accent)',
    borderRadius: '8px',
    backgroundColor: 'rgba(168,85,247,0.06)',
    color: 'var(--bln-accent)',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: FB,
    letterSpacing: '0.2px',
  },
  roleInfo: {
    fontSize: '12px',
    color: 'var(--bln-text-muted)',
    textAlign: 'center',
    marginBottom: '1.5rem',
    padding: '8px 12px',
    background: 'rgba(168,85,247,0.04)',
    borderRadius: '8px',
    border: '1px solid var(--bln-accent-tint)',
    fontWeight: 300,
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  input: {
    padding: '12px 14px',
    borderRadius: '8px',
    border: '1px solid var(--bln-accent-tint)',
    backgroundColor: 'transparent',
    color: 'var(--bln-text)',
    fontSize: '14px',
    fontFamily: FB,
    fontWeight: 300,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  btnPrimary: {
    padding: '13px',
    borderRadius: '8px',
    border: 'none',
    background: 'var(--bln-accent)',
    color: '#fff',
    fontWeight: 500,
    fontSize: '14px',
    fontFamily: FB,
    cursor: 'pointer',
    marginTop: '4px',
    letterSpacing: '0.2px',
  },
  error: {
    color: 'var(--bln-danger)',
    fontSize: '13px',
    margin: 0,
    padding: '8px 12px',
    background: 'rgba(248,113,113,0.06)',
    borderRadius: '8px',
    border: '1px solid rgba(248,113,113,0.15)',
    fontWeight: 300,
  },
  link: {
    color: 'var(--bln-text-muted)',
    textAlign: 'center',
    marginTop: '1.25rem',
    fontSize: '13px',
    fontWeight: 300,
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--bln-text-muted)',
    cursor: 'pointer',
    fontSize: '13px',
    padding: '0 0 1.25rem 0',
    display: 'block',
    fontFamily: FB,
    fontWeight: 300,
    letterSpacing: '0.2px',
  },
  srOnly: {
    position: 'absolute',
    width: '1px', height: '1px',
    padding: 0, margin: '-1px',
    overflow: 'hidden', clip: 'rect(0,0,0,0)',
    whiteSpace: 'nowrap', border: 0,
  },
}
